import React from 'react';
import PropTypes from 'prop-types';
import { Form, Input, Select, DatePicker, message } from 'antd';
import { UploadOutlined, CloseOutlined, FilePdfOutlined, EyeOutlined } from '@ant-design/icons';
// eslint-disable-next-line import/no-extraneous-dependencies
import dayjs from 'dayjs';
import { Flex, Text } from 'atoms';
import {
	FormContainer,
	SectionTitle,
	SectionSubtitle,
	FormRow,
	FormItem,
	BrowseButton,
	AddDriverButton,
	HeaderSection,
	FileUploadContainer,
	BulkUploadButton,
} from '../CreateDriverPageV2.styles';
import {
	ALLOWED_FILE_TYPES,
	MAX_FILE_SIZE,
	FILE_TYPE_ERROR_MSG,
	FILE_SIZE_ERROR_MSG,
	DL_NUMBER_REQUIRED_MSG,
	UPLOAD_FAILED_MSG,
	FILE_UPLOAD_ERROR_LOG,
	TEXT_COLOR_DARK,
	FONT_SIZE_SMALL,
	BUTTON_TEXT_UPLOADING,
	BUTTON_TEXT_BROWSE,
	FILE_DISPLAY_CONTAINER_STYLE,
	FILE_ICON_STYLE,
	CLOSE_ICON_STYLE,
} from '../CreateDriverPageV2.constants';

const { Option } = Select;
const ALL_VEHICLE_TYPES_VALUE = '__ALL_VEHICLE_TYPES__';

export default function CreateDriverForm({
	form,
	vendorOptions,
	vehicleTypeOptions,
	onVendorChange,
	drivingLicenseFile,
	setDrivingLicenseFile,
	drivingLicenseFileKey,
	setDrivingLicenseFileKey,
	uploadingLicense,
	setUploadingLicense,
	fileInputRef,
	dispatch,
	getSignedUrl,
	uploadFilesToS3,
	driversList,
	setDriversList,
	onBulkUpload,
	onViewDocument,
}) {
	// Handle driving license file selection
	const handleFileSelect = () => {
		fileInputRef.current?.click();
	};

	// Handle driving license file change
	const handleFileChange = async (event) => {
		const file = event.target.files?.[0];
		if (!file) return;

		// Validate file type
		if (!ALLOWED_FILE_TYPES.includes(file.type)) {
			message.error(FILE_TYPE_ERROR_MSG);
			return;
		}

		// Validate file size (5MB)
		if (file.size > MAX_FILE_SIZE) {
			message.error(FILE_SIZE_ERROR_MSG);
			return;
		}

		setDrivingLicenseFile(file);
		setUploadingLicense(true);

		try {
			// Get signed URL
			const signedUrlResponse = await dispatch(
				getSignedUrl({
					url_type: 'upload',
					data: {
						entity: 'driver',
						keys: [file.name],
					},
				})
			);

			if (signedUrlResponse?.data?.[0]) {
				const signedUrl = signedUrlResponse.data[0];

				// Upload to S3
				await dispatch(
					uploadFilesToS3({
						url: signedUrl.url,
						data: file,
					})
				);

				// Store the file key
				setDrivingLicenseFileKey(signedUrl.filename);
				setUploadingLicense(false);
				message.success('Driving license uploaded successfully');
			}
		} catch (error) {
			console.error(FILE_UPLOAD_ERROR_LOG, error);
			setUploadingLicense(false);
			message.error(`${UPLOAD_FAILED_MSG} driving license`);
		}
	};

	// Handle add driver
	const handleAddDriver = async () => {
		try {
			const values = await form.validateFields([
				'vendor_id',
				'vehicle_type_ids',
				'driver_name',
				'mobile_number',
				'driving_license_number',
				'driving_license_valid_till',
			]);

			// Validate vendor is selected
			if (!values.vendor_id) {
				message.error('Please select a vendor');
				return;
			}

			// Validate vehicle type is selected
			if (!values.vehicle_type_ids || values.vehicle_type_ids.length === 0) {
				message.error('Please select a vehicle type');
				return;
			}

			const selectedVehicleTypeIds = (values.vehicle_type_ids || []).filter(
				(typeId) => typeId !== ALL_VEHICLE_TYPES_VALUE
			);

			// Validate vehicle type belongs to selected vendor
			const isValidVehicleType = selectedVehicleTypeIds.every((val) =>
				vehicleTypeOptions.some((option) => option.value === val)
			);
			if (!isValidVehicleType) {
				message.error(
					'Selected vehicle type is not associated with the selected vendor. Please select a valid vehicle type.'
				);
				form.setFields([
					{
						name: 'vehicle_type_ids',
						errors: [
							'Selected vehicle type is not associated with the selected vendor',
						],
					},
				]);
				return;
			}

			// Validate driving license file
			if (!drivingLicenseFileKey) {
				message.error('Please upload driving license document');
				return;
			}

			// Validate driving license number
			if (!values.driving_license_number) {
				message.error(DL_NUMBER_REQUIRED_MSG);
				return;
			}

			const newDriver = {
				id: Date.now(), // Temporary ID
				vendor_id: values.vendor_id,
				vendor_name: vendorOptions.find((v) => v.value === values.vendor_id)?.label || '-',
				vehicle_type_ids: selectedVehicleTypeIds,
				vehicle_type_name:
					selectedVehicleTypeIds
						.map((id) => vehicleTypeOptions.find((v) => v.value === id)?.label)
						.filter(Boolean)
						.join(', ') || '-',
				driver_name: values.driver_name,
				mobile_number: values.mobile_number,
				driving_license_file: drivingLicenseFileKey,
				driving_license_number: values.driving_license_number,
				driving_license_valid_till: values.driving_license_valid_till
					? dayjs(values.driving_license_valid_till).format('YYYY-MM-DD')
					: null,
			};

			setDriversList([...driversList, newDriver]);
			form.resetFields();
			setDrivingLicenseFile(null);
			setDrivingLicenseFileKey(null);
			if (fileInputRef.current) {
				fileInputRef.current.value = '';
			}
			message.success('Driver added to list');
		} catch (error) {
			console.error('Validation failed:', error);
		}
	};

	const handleVehicleTypeChange = (selectedValues = []) => {
		if (!selectedValues.includes(ALL_VEHICLE_TYPES_VALUE)) {
			return;
		}

		form.setFieldsValue({
			vehicle_type_ids: vehicleTypeOptions.map((type) => type.value),
		});
	};

	return (
		<FormContainer>
			<HeaderSection>
				<div>
					<SectionTitle>Create Driver</SectionTitle>
					<SectionSubtitle>Add new drivers here</SectionSubtitle>
				</div>
				<BulkUploadButton onClick={onBulkUpload} icons={<UploadOutlined />}>
					BULK UPLOAD
				</BulkUploadButton>
			</HeaderSection>

			<Form form={form} layout="vertical">
				<FormRow>
					<FormItem>
						<Form.Item
							label="Select Vendor"
							name="vendor_id"
							rules={[{ required: true, message: 'Please select vendor' }]}
						>
							<Select
								placeholder="Select"
								size="large"
								showSearch
								filterOption={(input, option) =>
									String(option.children)
										.toLowerCase()
										.includes(input.toLowerCase())
								}
								onChange={onVendorChange}
							>
								{vendorOptions.map((vendor) => (
									<Option key={vendor.value} value={vendor.value}>
										{vendor.label}
									</Option>
								))}
							</Select>
						</Form.Item>
					</FormItem>
					<FormItem>
						<Form.Item
							label="Driver Name"
							name="driver_name"
							rules={[{ required: true, message: 'Please enter driver name' }]}
						>
							<Input
								placeholder="Eg : Venkatesh......."
								size="large"
								maxLength={255}
							/>
						</Form.Item>
					</FormItem>
				</FormRow>

				<FormRow>
					<FormItem>
						<Form.Item
							label="Vehicle Type"
							name="vehicle_type_ids"
							rules={[
								{ required: true, message: 'Please select vehicle type' },
								{
									validator: (_, value) => {
										if (!value || value.length === 0) {
											return Promise.resolve();
										}
										const filteredValue = value.filter(
											(val) => val !== ALL_VEHICLE_TYPES_VALUE
										);
										const isValid = filteredValue.every((val) =>
											vehicleTypeOptions.some(
												(option) => option.value === val
											)
										);
										if (!isValid) {
											return Promise.reject(
												new Error(
													'Selected vehicle type is not associated with the selected vendor'
												)
											);
										}
										return Promise.resolve();
									},
								},
							]}
							dependencies={['vendor_id']}
						>
							<Select
								mode="multiple"
								placeholder="Select"
								size="large"
								showSearch
								onChange={handleVehicleTypeChange}
								filterOption={(input, option) =>
									String(option.children)
										.toLowerCase()
										.includes(input.toLowerCase())
								}
							>
								{vehicleTypeOptions.length > 0 && (
									<Option value={ALL_VEHICLE_TYPES_VALUE}>All</Option>
								)}
								{vehicleTypeOptions.map((type) => (
									<Option key={type.value} value={type.value}>
										{type.label}
									</Option>
								))}
							</Select>
						</Form.Item>
					</FormItem>
					<FormItem>
						<Form.Item
							label="Mobile Number"
							name="mobile_number"
							rules={[
								{ required: true, message: 'Please enter mobile number' },
								{
									pattern: /^[0-9]{10}$/,
									message: 'Please enter a valid 10-digit mobile number',
								},
							]}
						>
							<Input placeholder="Eg : 9876545678" size="large" maxLength={20} />
						</Form.Item>
					</FormItem>
				</FormRow>

				<FormRow>
					<FormItem>
						<Form.Item
							label="Driving License Number"
							name="driving_license_number"
							rules={[
								{
									required: true,
									message: DL_NUMBER_REQUIRED_MSG,
								},
								{
									validator(_, value) {
										if (value && value !== value.trimStart()) {
											return Promise.reject(
												new Error(
													'Driving license number should not have leading spaces'
												)
											);
										}
										return Promise.resolve();
									},
								},
							]}
						>
							<Input placeholder="Eg : DL0208735" size="large" />
						</Form.Item>
					</FormItem>
					<FormItem>
						<Form.Item
							label="Driving License Upload"
							required
							rules={[
								{
									validator: () => {
										if (!drivingLicenseFileKey) {
											return Promise.reject(
												new Error('Please upload driving license')
											);
										}
										return Promise.resolve();
									},
								},
							]}
						>
							<FileUploadContainer>
								<input
									ref={fileInputRef}
									type="file"
									accept=".pdf,.png,.jpeg,.jpg"
									onChange={handleFileChange}
									style={{ display: 'none' }}
								/>
								<BrowseButton
									type="button"
									icon={<UploadOutlined />}
									onClick={handleFileSelect}
									loading={uploadingLicense}
									disabled={uploadingLicense}
								>
									{uploadingLicense ? BUTTON_TEXT_UPLOADING : BUTTON_TEXT_BROWSE}
								</BrowseButton>
								{drivingLicenseFile && (
									<Flex
										alignItems="center"
										justifyContent="space-between"
										mt={2}
										p={2}
										style={FILE_DISPLAY_CONTAINER_STYLE}
									>
										<Flex
											alignItems="center"
											gap={2}
											style={{ cursor: 'pointer', flex: 1 }}
											onClick={() => {
												if (drivingLicenseFileKey && onViewDocument) {
													onViewDocument(
														drivingLicenseFileKey,
														'Driving License'
													);
												}
											}}
										>
											<FilePdfOutlined style={FILE_ICON_STYLE} />
											<Text
												fontSize={FONT_SIZE_SMALL}
												color={TEXT_COLOR_DARK}
											>
												{drivingLicenseFile.name}
											</Text>
											{drivingLicenseFileKey && onViewDocument && (
												<EyeOutlined
													style={{
														fontSize: 16,
														color: '#1890ff',
														marginLeft: 8,
													}}
													onClick={(e) => {
														e.stopPropagation();
														onViewDocument(
															drivingLicenseFileKey,
															'Driving License'
														);
													}}
												/>
											)}
										</Flex>
										<CloseOutlined
											style={CLOSE_ICON_STYLE}
											onClick={(e) => {
												e.stopPropagation();
												setDrivingLicenseFile(null);
												setDrivingLicenseFileKey(null);
												if (fileInputRef.current) {
													fileInputRef.current.value = '';
												}
											}}
										/>
									</Flex>
								)}
							</FileUploadContainer>
						</Form.Item>
					</FormItem>
				</FormRow>

				<FormRow>
					<FormItem>
						<Form.Item
							label="Valid Till"
							name="driving_license_valid_till"
							rules={[{ required: true, message: 'Please select valid till date' }]}
						>
							<DatePicker
								placeholder="Choose Date"
								size="large"
								style={{ width: '100%' }}
								format="DD-MM-YYYY"
								disabledDate={(current) =>
									current && current < dayjs().startOf('day')
								}
							/>
						</Form.Item>
					</FormItem>
					<FormItem>{/* Empty column for alignment */}</FormItem>
				</FormRow>

				<Flex justifyContent="center" mt={3}>
					<AddDriverButton type="button" onClick={handleAddDriver}>
						Add Driver
					</AddDriverButton>
				</Flex>
			</Form>
		</FormContainer>
	);
}

CreateDriverForm.propTypes = {
	form: PropTypes.object.isRequired,
	vendorOptions: PropTypes.array.isRequired,
	vehicleTypeOptions: PropTypes.array.isRequired,
	onVendorChange: PropTypes.func.isRequired,
	drivingLicenseFile: PropTypes.object,
	setDrivingLicenseFile: PropTypes.func.isRequired,
	drivingLicenseFileKey: PropTypes.string,
	setDrivingLicenseFileKey: PropTypes.func.isRequired,
	uploadingLicense: PropTypes.bool.isRequired,
	setUploadingLicense: PropTypes.func.isRequired,
	fileInputRef: PropTypes.object.isRequired,
	dispatch: PropTypes.func.isRequired,
	getSignedUrl: PropTypes.func.isRequired,
	uploadFilesToS3: PropTypes.func.isRequired,
	driversList: PropTypes.array.isRequired,
	setDriversList: PropTypes.func.isRequired,
	onBulkUpload: PropTypes.func.isRequired,
	onViewDocument: PropTypes.func,
};
